/**
 * Document Capture Module for AppBouncer SDK
 * Handles ID document capture, validation, and upload
 */

import { DEFAULT_ENDPOINTS, DEFAULT_TIMEOUTS } from '../config';

// ============================================================
// Types
// ============================================================

export type DocumentType =
  | 'government_id'
  | 'driver_license'
  | 'passport'
  | 'national_id'
  | 'residence_permit';

export type DocumentSide = 'front' | 'back';

export interface DocumentConfig {
  /** API base URL */
  apiBaseUrl: string;
  /** API key for authentication */
  apiKey?: string;
  /** Presign endpoint path (default: /v1/ocr/presign) */
  presignEndpoint?: string;
  /** Verify URLs endpoint path (default: /v1/ocr/verify-urls) */
  verifyUrlsEndpoint?: string;
  /** JPEG quality for captures (default: 0.9) */
  jpegQuality?: number;
  /** Maximum file size in bytes (default: 5MB) */
  maxFileSize?: number;
  /** Include credentials in requests (default: true) */
  includeCredentials?: boolean;
  /** Timeout for uploads in ms (default: 30000) */
  uploadTimeout?: number;
}

export interface CapturedDocument {
  /** Document type */
  type: DocumentType;
  /** Which side of the document */
  side: DocumentSide;
  /** Country code (ISO 2-letter) */
  countryCode: string;
  /** Image blob */
  blob: Blob;
  /** Data URL for preview */
  dataUrl: string;
  /** Capture timestamp */
  capturedAt: number;
  /** Image dimensions */
  dimensions: { width: number; height: number };
}

export interface DocumentUploadResponse {
  success: boolean;
  document_id?: string;
  extracted_data?: {
    first_name?: string;
    last_name?: string;
    date_of_birth?: string;
    document_number?: string;
    expiry_date?: string;
    nationality?: string;
    [key: string]: string | undefined;
  };
  confidence?: number;
  error?: string;
}

export interface DocumentVerificationResult {
  success: boolean;
  verified: boolean;
  session_id?: string;
  task_id?: string;
  front_document_id?: string;
  back_document_id?: string;
  extracted_data?: Record<string, string>;
  confidence?: number;
  errors?: string[];
}

// ============================================================
// Presigned URL Types
// ============================================================

export interface PresignedUrlData {
  /** Presigned PUT URL for uploading directly to S3 */
  upload_url: string;
  /** Public URL of the object after upload completes */
  object_url: string;
  /** S3 key for reference */
  key: string;
  /** Seconds until URL expires */
  expires_in: number;
  /** Expected content type */
  content_type: string;
}

export interface PresignResponse {
  code: number;
  success: boolean;
  /** Unique session ID for this upload set */
  session_id: string;
  /** Presigned URL for front document */
  front: PresignedUrlData;
  /** Presigned URL for first selfie (captured during face detection countdown) */
  selfie_1: PresignedUrlData;
  /** Presigned URL for second selfie (captured during age estimation) */
  selfie_2: PresignedUrlData;
  /** Presigned URL for back document (optional) */
  back?: PresignedUrlData;
}

export interface VerifyUrlsRequest {
  /** Session ID from presign response */
  session_id: string;
  /** S3 URL of front document image */
  front_url: string;
  /** S3 URL of first selfie (face detection capture) */
  selfie_1_url: string;
  /** S3 URL of second selfie (age estimation capture) */
  selfie_2_url: string;
  /** S3 URL of back document image (optional) */
  back_url?: string;
  /** Document type */
  document_type: string;
  /** Verification session ID from /requirements/check (optional) */
  verification_session_id?: string;
}

export interface DocumentCaptureCallbacks {
  /** Called when capture state changes */
  onStateChange?: (state: DocumentCaptureState) => void;
  /** Called when a capture is completed */
  onCaptureComplete?: (document: CapturedDocument) => void;
  /** Called during upload progress */
  onUploadProgress?: (progress: number, side: DocumentSide) => void;
  /** Called on upload success */
  onUploadSuccess?: (response: DocumentUploadResponse, side: DocumentSide) => void;
  /** Called on upload error */
  onUploadError?: (error: Error, side: DocumentSide) => void;
  /** Called with status messages */
  onStatusMessage?: (message: string) => void;
}

export type DocumentCaptureState =
  | 'idle'
  | 'selecting' // Selecting country/type
  | 'ready_front' // Ready to capture front
  | 'capturing_front' // Taking front photo
  | 'preview_front' // Reviewing front capture
  | 'uploading_front' // Uploading front
  | 'ready_back' // Ready to capture back
  | 'capturing_back' // Taking back photo
  | 'preview_back' // Reviewing back capture
  | 'uploading_back' // Uploading back
  | 'processing' // Server processing
  | 'success' // Verification complete
  | 'error'; // Error state

export interface CountryInfo {
  code: string;
  name: string;
  supportedDocuments: DocumentType[];
}

// ============================================================
// Default Configuration
// ============================================================

export const DEFAULT_DOCUMENT_CONFIG: Required<Omit<DocumentConfig, 'apiBaseUrl' | 'apiKey'>> & {
  apiBaseUrl: string;
  apiKey: string;
} = {
  apiBaseUrl: '',
  apiKey: '',
  presignEndpoint: DEFAULT_ENDPOINTS.ocr.presign,
  verifyUrlsEndpoint: DEFAULT_ENDPOINTS.ocr.verifyUrls,
  jpegQuality: 0.9,
  maxFileSize: 5 * 1024 * 1024, // 5MB
  includeCredentials: true,
  uploadTimeout: DEFAULT_TIMEOUTS.upload,
};

// ============================================================
// Photo Tips
// ============================================================

export const PHOTO_TIPS = [
  'Place your ID on a flat, dark surface',
  'Avoid bright backlighting',
  'Slowly move the ID closer to the camera until a picture is taken',
  'Ensure the entire ID is fully visible',
  'Avoid glare and reflections',
];

// ============================================================
// Supported Countries (can be extended)
// ============================================================

export const SUPPORTED_COUNTRIES: CountryInfo[] = [
  {
    code: 'US',
    name: 'United States',
    supportedDocuments: ['driver_license', 'passport', 'government_id'],
  },
  {
    code: 'CA',
    name: 'Canada',
    supportedDocuments: ['driver_license', 'passport', 'government_id'],
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    supportedDocuments: ['driver_license', 'passport', 'national_id'],
  },
  {
    code: 'DE',
    name: 'Germany',
    supportedDocuments: ['driver_license', 'passport', 'national_id'],
  },
  { code: 'FR', name: 'France', supportedDocuments: ['driver_license', 'passport', 'national_id'] },
  { code: 'AU', name: 'Australia', supportedDocuments: ['driver_license', 'passport'] },
  {
    code: 'JP',
    name: 'Japan',
    supportedDocuments: ['driver_license', 'passport', 'residence_permit'],
  },
  // Add more countries as needed
];

// ============================================================
// Document Type Display Names
// ============================================================

export const DOCUMENT_TYPE_NAMES: Record<DocumentType, string> = {
  government_id: 'Government ID',
  driver_license: 'Driver License',
  passport: 'Passport',
  national_id: 'National ID',
  residence_permit: 'Residence Permit',
};

// ============================================================
// Crop Region Calculation
// ============================================================

/**
 * Defines a rectangular region for cropping
 */
export interface CropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculate crop region from video frame to achieve target aspect ratio.
 *
 * For ID cards, we use 16:10 ratio (ISO ID card: 85.6mm × 53.98mm).
 * The crop is centered on the video frame.
 *
 * @param videoWidth - Width of the video frame in pixels
 * @param videoHeight - Height of the video frame in pixels
 * @param targetAspectRatio - Target width:height ratio (default: 1.6 for 16:10)
 * @returns CropRegion with x, y offset and dimensions
 */
export function calculateCropRegion(
  videoWidth: number,
  videoHeight: number,
  targetAspectRatio: number = 1.6 // 16:10 ISO ID card ratio
): CropRegion {
  // Start with full width and calculate height for target ratio
  let cropWidth = videoWidth;
  let cropHeight = cropWidth / targetAspectRatio;

  // If crop height exceeds video height, constrain by height instead
  if (cropHeight > videoHeight) {
    cropHeight = videoHeight;
    cropWidth = cropHeight * targetAspectRatio;
  }

  // Center the crop region
  const x = (videoWidth - cropWidth) / 2;
  const y = (videoHeight - cropHeight) / 2;

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(cropWidth),
    height: Math.round(cropHeight),
  };
}

// ============================================================
// Document Capture Class
// ============================================================

export class DocumentCapture {
  private config: Required<DocumentConfig>;
  private callbacks: DocumentCaptureCallbacks | null = null;
  private state: DocumentCaptureState = 'idle';

  // Current capture session
  private selectedCountry: string | null = null;
  private selectedDocumentType: DocumentType | null = null;
  private frontCapture: CapturedDocument | null = null;
  private backCapture: CapturedDocument | null = null;
  private frontDocumentId: string | null = null;
  private backDocumentId: string | null = null;

  // Face/selfie images for document verification (2 selfies for training data)
  private faceBlob1: Blob | null = null; // Selfie from face detection countdown
  private faceBlob2: Blob | null = null; // Selfie from age estimation

  // Age metadata for training data naming convention
  private selfDeclaredAge: number = 0;
  private estimatedAge: number = 0;

  // Presigned URL session
  private presignSession: PresignResponse | null = null;

  // Verification session ID (from /requirements/check)
  private verificationSessionId: string | null = null;

  constructor(config: DocumentConfig) {
    this.config = {
      ...DEFAULT_DOCUMENT_CONFIG,
      ...config,
    };
  }

  /**
   * Set both face/selfie image blobs for document verification
   * This should be called after capturing selfies at face detection and age estimation
   *
   * @param blob1 First selfie captured during face detection countdown
   * @param blob2 Second selfie captured during age estimation
   */
  setFaceImages(blob1: Blob, blob2: Blob): void {
    this.faceBlob1 = blob1;
    this.faceBlob2 = blob2;
  }

  /**
   * @deprecated Use setFaceImages() instead for dual selfie support
   * Set a single face image (for backwards compatibility)
   */
  setFaceImage(blob: Blob): void {
    // For backwards compatibility, set as second selfie (age estimation)
    this.faceBlob2 = blob;
  }

  /**
   * Set age metadata for selfie photo naming convention.
   *
   * Naming pattern: `sd{X}_es{Y}_ocr{Z}.jpg`
   * - sd = self-declared age (always 0, self-declaration removed)
   * - es = estimated age from ONNX model (0 if not performed)
   * - ocr = age extracted from document OCR (0 initially, backend updates after processing)
   *
   * Examples:
   * - `sd0_es40_ocr0.jpg` - age estimated at 40, OCR pending
   * - `sd0_es40_ocr42.jpg` - after OCR extracts age 42 from document
   *
   * @param selfDeclaredAge Always 0 (self-declaration removed)
   * @param estimatedAge Age from ONNX model (0 if age estimation not performed)
   */
  setAgeMetadata(selfDeclaredAge: number, estimatedAge: number): void {
    this.selfDeclaredAge = Math.round(selfDeclaredAge) || 0;
    this.estimatedAge = Math.round(estimatedAge) || 0;
  }

  /**
   * Set the verification session ID from /requirements/check
   */
  setVerificationSessionId(sessionId: string): void {
    this.verificationSessionId = sessionId;
  }

  /**
   * Set callbacks for UI updates
   */
  setCallbacks(callbacks: DocumentCaptureCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Get current state
   */
  getState(): DocumentCaptureState {
    return this.state;
  }

  /**
   * Set state and notify listeners
   */
  private setState(newState: DocumentCaptureState): void {
    this.state = newState;
    this.callbacks?.onStateChange?.(newState);
  }

  /**
   * Get supported countries
   */
  getSupportedCountries(): CountryInfo[] {
    return SUPPORTED_COUNTRIES;
  }

  /**
   * Get document types for a country
   */
  getDocumentTypesForCountry(countryCode: string): DocumentType[] {
    const country = SUPPORTED_COUNTRIES.find((c) => c.code === countryCode);
    return country?.supportedDocuments || [];
  }

  /**
   * Start document capture flow
   */
  startCapture(): void {
    this.reset();
    this.setState('selecting');
  }

  /**
   * Select country and document type
   */
  selectDocument(countryCode: string, documentType: DocumentType): void {
    this.selectedCountry = countryCode;
    this.selectedDocumentType = documentType;
    this.setState('ready_front');
    this.callbacks?.onStatusMessage?.(
      `Ready to capture ${DOCUMENT_TYPE_NAMES[documentType]} (Front)`
    );
  }

  /**
   * Capture document from video element.
   *
   * Crops the video frame to a 16:10 aspect ratio (ISO ID card standard)
   * centered on the frame. This ensures the captured image matches the
   * visible guide frame overlay.
   */
  async captureFromVideo(
    video: HTMLVideoElement,
    side: DocumentSide
  ): Promise<CapturedDocument | null> {
    if (!this.selectedCountry || !this.selectedDocumentType) {
      throw new Error('Country and document type must be selected first');
    }

    this.setState(side === 'front' ? 'capturing_front' : 'capturing_back');

    // Calculate crop region for 16:10 aspect ratio (ISO ID card)
    const cropRegion = calculateCropRegion(video.videoWidth, video.videoHeight, 1.6);

    // Create canvas with cropped dimensions (landscape oriented)
    const canvas = document.createElement('canvas');
    canvas.width = cropRegion.width;
    canvas.height = cropRegion.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Cannot get canvas context');
    }

    // Draw ONLY the cropped region from the video
    // drawImage(source, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
    ctx.drawImage(
      video,
      cropRegion.x,
      cropRegion.y, // Source position (crop offset)
      cropRegion.width,
      cropRegion.height, // Source size (crop dimensions)
      0,
      0, // Destination position
      canvas.width,
      canvas.height // Destination size
    );

    // Convert to blob
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', this.config.jpegQuality);
    });

    if (!blob) {
      throw new Error('Failed to capture image');
    }

    // Check file size
    if (blob.size > this.config.maxFileSize) {
      throw new Error(
        `Image too large (${Math.round(blob.size / 1024 / 1024)}MB). Maximum is ${Math.round(this.config.maxFileSize / 1024 / 1024)}MB.`
      );
    }

    // Create data URL for preview
    const dataUrl = canvas.toDataURL('image/jpeg', this.config.jpegQuality);

    const capturedDoc: CapturedDocument = {
      type: this.selectedDocumentType,
      side,
      countryCode: this.selectedCountry,
      blob,
      dataUrl,
      capturedAt: Date.now(),
      dimensions: { width: canvas.width, height: canvas.height },
    };

    // Store capture
    if (side === 'front') {
      this.frontCapture = capturedDoc;
      this.setState('preview_front');
    } else {
      this.backCapture = capturedDoc;
      this.setState('preview_back');
    }

    this.callbacks?.onCaptureComplete?.(capturedDoc);

    return capturedDoc;
  }

  /**
   * Capture from file input
   */
  async captureFromFile(file: File, side: DocumentSide): Promise<CapturedDocument | null> {
    if (!this.selectedCountry || !this.selectedDocumentType) {
      throw new Error('Country and document type must be selected first');
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select an image file');
    }

    // Check file size
    if (file.size > this.config.maxFileSize) {
      throw new Error(
        `Image too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is ${Math.round(this.config.maxFileSize / 1024 / 1024)}MB.`
      );
    }

    this.setState(side === 'front' ? 'capturing_front' : 'capturing_back');

    // Load image to get dimensions
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUrl;
    });

    const capturedDoc: CapturedDocument = {
      type: this.selectedDocumentType,
      side,
      countryCode: this.selectedCountry,
      blob: file,
      dataUrl,
      capturedAt: Date.now(),
      dimensions: { width: img.width, height: img.height },
    };

    // Store capture
    if (side === 'front') {
      this.frontCapture = capturedDoc;
      this.setState('preview_front');
    } else {
      this.backCapture = capturedDoc;
      this.setState('preview_back');
    }

    this.callbacks?.onCaptureComplete?.(capturedDoc);

    return capturedDoc;
  }

  /**
   * Retake current capture
   */
  retake(side: DocumentSide): void {
    if (side === 'front') {
      this.frontCapture = null;
      this.frontDocumentId = null;
      this.setState('ready_front');
    } else {
      this.backCapture = null;
      this.backDocumentId = null;
      this.setState('ready_back');
    }
  }

  /**
   * Confirm capture and proceed to next step
   */
  confirmCapture(side: DocumentSide): void {
    if (side === 'front') {
      if (!this.frontCapture) {
        throw new Error('No front capture to confirm');
      }
      // Check if document type requires back
      const requiresBack = this.selectedDocumentType !== 'passport';
      if (requiresBack) {
        this.setState('ready_back');
        this.callbacks?.onStatusMessage?.(
          `Ready to capture ${DOCUMENT_TYPE_NAMES[this.selectedDocumentType!]} (Back)`
        );
      } else {
        // Passport only has one side, proceed to upload
        this.uploadDocuments();
      }
    } else {
      if (!this.backCapture) {
        throw new Error('No back capture to confirm');
      }
      this.uploadDocuments();
    }
  }

  /**
   * Skip back capture (for documents that don't require it)
   */
  skipBackCapture(): void {
    this.uploadDocuments();
  }

  /**
   * Get presigned URLs for direct S3 upload
   *
   * Includes age metadata for training data naming convention:
   * - self_declared_age: From DOB picker (self-declaration)
   * - estimated_age: From ONNX model estimation
   *
   * The backend will generate face image filename: sd{self_declared}_es{estimated}.jpg
   */
  private async getPresignedUrls(): Promise<PresignResponse> {
    const url = `${this.config.apiBaseUrl}${this.config.presignEndpoint}`;

    // Map internal document type to backend format
    const backendDocType = this.mapDocumentType(this.selectedDocumentType!);
    const includeBack = this.selectedDocumentType !== 'passport';

    const response = await fetch(url, {
      method: 'POST',
      credentials: this.config.includeCredentials ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { 'X-API-Key': this.config.apiKey }),
      },
      body: JSON.stringify({
        document_type: backendDocType,
        include_back: includeBack,
        // Age metadata for selfie naming: sd{X}_es{Y}_ocr{Z}.jpg
        // - self_declared_age: always 0 (self-declaration removed)
        // - estimated_age: from ONNX model (0 if age estimation not done)
        // - ocr_age: 0 initially, backend updates after OCR extraction
        self_declared_age: this.selfDeclaredAge,
        estimated_age: this.estimatedAge,
        ocr_age: 0, // Always 0 on upload, backend updates after OCR processing
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to get upload URLs: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Map SDK document type to backend document type
   */
  private mapDocumentType(type: DocumentType): string {
    const mapping: Record<DocumentType, string> = {
      government_id: 'id_card',
      driver_license: 'drivers_license',
      passport: 'passport',
      national_id: 'id_card',
      residence_permit: 'id_card',
    };
    return mapping[type] || 'id_card';
  }

  /**
   * Upload a blob directly to S3 using a presigned URL
   */
  private async uploadToS3(
    presignedUrl: string,
    blob: Blob,
    contentType: string = 'image/jpeg'
  ): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.uploadTimeout);

    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: blob,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`S3 upload failed: ${response.status}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Upload timed out');
      }
      throw error;
    }
  }

  /**
   * Upload all captured documents using presigned URLs (direct to S3)
   */
  async uploadDocuments(): Promise<void> {
    this.setState('processing');
    this.callbacks?.onStatusMessage?.('Preparing upload...');

    // Validate we have required images
    if (!this.frontCapture) {
      throw new Error('No front document captured');
    }
    if (!this.faceBlob1 || !this.faceBlob2) {
      throw new Error(
        'Both face images required. Please complete face detection and age estimation first.'
      );
    }

    try {
      // Step 1: Get presigned URLs from backend
      this.callbacks?.onStatusMessage?.('Getting upload URLs...');
      this.presignSession = await this.getPresignedUrls();

      // Step 2: Upload front document directly to S3
      this.setState('uploading_front');
      this.callbacks?.onUploadProgress?.(0, 'front');
      this.callbacks?.onStatusMessage?.('Uploading front document...');

      await this.uploadToS3(this.presignSession.front.upload_url, this.frontCapture.blob);

      this.callbacks?.onUploadProgress?.(100, 'front');
      this.callbacks?.onUploadSuccess?.({ success: true }, 'front');

      // Step 3: Upload back document if captured
      if (this.backCapture && this.presignSession.back) {
        this.setState('uploading_back');
        this.callbacks?.onUploadProgress?.(0, 'back');
        this.callbacks?.onStatusMessage?.('Uploading back document...');

        await this.uploadToS3(this.presignSession.back.upload_url, this.backCapture.blob);

        this.callbacks?.onUploadProgress?.(100, 'back');
        this.callbacks?.onUploadSuccess?.({ success: true }, 'back');
      }

      // Step 4: Upload first selfie (face detection capture)
      this.callbacks?.onStatusMessage?.('Uploading face images...');
      await this.uploadToS3(this.presignSession.selfie_1.upload_url, this.faceBlob1!);

      // Step 5: Upload second selfie (age estimation capture)
      await this.uploadToS3(this.presignSession.selfie_2.upload_url, this.faceBlob2!);

      // Step 6: Call verify-urls endpoint
      this.setState('processing');
      this.callbacks?.onStatusMessage?.('Processing documents...');

      const result = await this.verifyDocumentsWithUrls();

      if (result.success && result.task_id) {
        // Store task_id for polling
        this.frontDocumentId = result.task_id;
        this.setState('success');
        this.callbacks?.onStatusMessage?.('Document verification started!');
      } else {
        this.setState('error');
        this.callbacks?.onStatusMessage?.(result.errors?.[0] || 'Verification failed');
      }
    } catch (error) {
      this.setState('error');
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      this.callbacks?.onStatusMessage?.(errorMessage);
      throw error;
    }
  }

  /**
   * Verify documents using S3 URLs
   */
  private async verifyDocumentsWithUrls(): Promise<DocumentVerificationResult> {
    if (!this.presignSession) {
      throw new Error('No presign session available');
    }

    const url = `${this.config.apiBaseUrl}${this.config.verifyUrlsEndpoint}`;

    const payload: VerifyUrlsRequest = {
      session_id: this.presignSession.session_id,
      front_url: this.presignSession.front.object_url,
      selfie_1_url: this.presignSession.selfie_1.object_url,
      selfie_2_url: this.presignSession.selfie_2.object_url,
      document_type: this.mapDocumentType(this.selectedDocumentType!),
    };

    // Add back URL if available
    if (this.backCapture && this.presignSession.back) {
      payload.back_url = this.presignSession.back.object_url;
    }

    // Add verification session ID if available
    if (this.verificationSessionId) {
      payload.verification_session_id = this.verificationSessionId;
    }

    const response = await fetch(url, {
      method: 'POST',
      credentials: this.config.includeCredentials ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { 'X-API-Key': this.config.apiKey }),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        verified: false,
        errors: [errorData.detail || this.getDocumentErrorMessage(response.status)],
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      verified: data.success,
      task_id: data.task_id,
      session_id: this.presignSession.session_id,
    };
  }

  private getDocumentErrorMessage(httpStatus: number): string {
    switch (httpStatus) {
      case 400:
        return 'The document could not be processed. Please ensure the image is clear and try again.';
      case 401:
      case 403:
        return 'Your session has expired. Please refresh the page and try again.';
      case 413:
        return 'The image file is too large. Please capture a smaller image.';
      case 422:
        return 'The document image is not valid. Please use a JPEG or PNG image.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
        return 'The verification service is temporarily unavailable. Please try again in a few moments.';
      default:
        return 'Something went wrong while verifying your document. Please try again.';
    }
  }

  /**
   * Get the task ID for polling OCR status
   */
  getTaskId(): string | null {
    return this.frontDocumentId;
  }

  /**
   * Get current captured documents
   */
  getCapturedDocuments(): {
    front: CapturedDocument | null;
    back: CapturedDocument | null;
  } {
    return {
      front: this.frontCapture,
      back: this.backCapture,
    };
  }

  /**
   * Get selection info
   */
  getSelection(): {
    country: string | null;
    documentType: DocumentType | null;
  } {
    return {
      country: this.selectedCountry,
      documentType: this.selectedDocumentType,
    };
  }

  /**
   * Check if back capture is required for selected document type
   */
  isBackRequired(): boolean {
    return this.selectedDocumentType !== 'passport';
  }

  /**
   * Reset capture flow
   */
  reset(): void {
    this.selectedCountry = null;
    this.selectedDocumentType = null;
    this.frontCapture = null;
    this.backCapture = null;
    this.frontDocumentId = null;
    this.backDocumentId = null;
    this.faceBlob1 = null;
    this.faceBlob2 = null;
    this.selfDeclaredAge = 0;
    this.estimatedAge = 0;
    this.presignSession = null;
    this.verificationSessionId = null;
    this.setState('idle');
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create a document capture instance
 */
export function createDocumentCapture(config: DocumentConfig): DocumentCapture {
  return new DocumentCapture(config);
}

// ============================================================
// Document Quality Checks
// ============================================================

/**
 * Analyze image brightness
 */
export function analyzeImageBrightness(imageData: ImageData): number {
  const data = imageData.data;
  let total = 0;

  for (let i = 0; i < data.length; i += 4) {
    total += data[i] + data[i + 1] + data[i + 2];
  }

  return total / (imageData.width * imageData.height * 3);
}

/**
 * Check if image is too blurry (using Laplacian variance)
 */
export function isImageBlurry(imageData: ImageData, threshold = 100): boolean {
  const { data, width, height } = imageData;

  // Convert to grayscale and compute Laplacian
  const gray = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
  }

  // Compute Laplacian variance
  let variance = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const laplacian =
        gray[idx - width] + gray[idx - 1] + gray[idx + 1] + gray[idx + width] - 4 * gray[idx];
      variance += laplacian * laplacian;
      count++;
    }
  }

  variance /= count;
  return variance < threshold;
}

/**
 * Check document image quality
 */
export interface DocumentQualityResult {
  ok: boolean;
  brightness: number;
  isBlurry: boolean;
  issues: string[];
}

export function checkDocumentQuality(
  canvas: HTMLCanvasElement,
  options: {
    minBrightness?: number;
    maxBrightness?: number;
    blurThreshold?: number;
  } = {}
): DocumentQualityResult {
  const { minBrightness = 50, maxBrightness = 220, blurThreshold = 100 } = options;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      ok: false,
      brightness: 0,
      isBlurry: false,
      issues: ['Cannot analyze image'],
    };
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const brightness = analyzeImageBrightness(imageData);
  const isBlurry = isImageBlurry(imageData, blurThreshold);

  const issues: string[] = [];

  if (brightness < minBrightness) {
    issues.push('Image is too dark');
  }
  if (brightness > maxBrightness) {
    issues.push('Image is too bright');
  }
  if (isBlurry) {
    issues.push('Image is blurry');
  }

  return {
    ok: issues.length === 0,
    brightness,
    isBlurry,
    issues,
  };
}
